import { Box, CircularProgress, Dialog, Paper } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DelayedRedirect from "../../../../utils/DelayedRedirect";
import { formatOutlets } from "../MessageTemplates/utils/optionFormatters";
import { Transition } from "../../../../utils/ModalHelpers";
import AppBarComponent from "../../../Common/Headers/AppBarComponent";
import EnhancedDropdownComponent from "../../../Common/Dropdowns/EnhancedDropdownComponent";
import LanguageDropdown from "../../../Common/Dropdowns/LanguageDropdown";
import { LanguagesWithLanguageCode } from "../../../../utils/constants";
import SignableDocumentsCheckboxesSection from "./components/SignableDocumentsCheckboxesSection";
import SignableDocumentsDatePickerSection from "./components/SignableDocumentsDatePickerSection";
import SignableDocumentsDocumentEditorSection from "./components/SignableDocumentsDocumentEditorSection";
import SignableDocumentsDescriptionSection from "./components/SignableDocumentsDescriptionSection";
import {
  SignableDocumentByIdUrl,
  SignableDocumentsUrl,
} from "../../../../constants/apiUrls";
import appFetch from "../../../../api/appFetch";
import { useParams } from "react-router-dom";
import {
  DisplayMessage,
  GetFormattedDate,
  Uuidv4,
} from "../../../../services/helpers";
import ModifiedByInfo from "../../../Common/Footers/ModifiedByInfo";
import { CONTEXT_OPTIONS } from "./utils/constants";
import {
  CheckIfSasIsValid,
  GenerateSAS,
  UploadFileInStorage,
} from "../../../../services/uploadHelpers";
import { sanitizeAndFormatHTML, sanitizeHTML } from "../../../../utils/htmlHelpers";
import { useLocation } from "react-router-dom";
import SaveWithoutPreviewConfirmationModal from "../MessageTemplates/components/SaveWithoutPreviewConfirmationModal";

const SignableDocumentsCreation = ({
  resources,
  language,
  accessibleMoboOutlets,
  dealerGroupId,
}) => {
  const { documentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [description, setDescription] = useState("");
  const [selectedContext, setSelectedContext] = useState(null);
  const [descriptionTranslations, setDescriptionTranslations] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTill, setDateTill] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [isDefaultAccepted, setIsDefaultAccepted] = useState(true);
  const [isAcceptanceMandatory, setIsAcceptanceMandatory] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isWarranty, setIsWarranty] = useState(false);
  const [htmlEditorValue, setHtmlEditorValue] = useState("");
  const [documentPdf, setDocumentPdf] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [hasViewedPreview, setHasViewedPreview] = useState(true);
  const [showPreviewWarning, setShowPreviewWarning] = useState(false);
  const [modified, setModified] = useState({
    on: "",
    byId: "",
    byName: "",
    avatar: "",
  });
  const [originalHTMLDocument, setOriginalHTMLDocument] = useState(null);
  const [originalPDFDocument, setOriginalPDFDocument] = useState(null);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [errors, setErrors] = useState(() => {
    if(documentId) {
      return {
				description: false,
				context: false,
        language: false,
			};
    }

    const descriptionError = !description || description.trim() === "";
    const contextError = !selectedContext;
    const languageError = !selectedLanguage

    return {
      description: descriptionError,
      context: contextError,
      language: languageError
    }
  })
  const location = useLocation()

  const accessibleMoboOutletsOptions = useMemo(
    () => formatOutlets(accessibleMoboOutlets),
    [accessibleMoboOutlets]
  );

  useEffect(() => {
    async function getData() {
      if (!documentId) {
        setSelectedLanguage(language);
        setErrors(prev => ({
          ...prev,
          language: false
        }))

        if(location.state?.outletId) {
          setSelectedOutlet(location.state.outletId)
        }

        return;
      }

      setLoading(true);

      const url = SignableDocumentByIdUrl(documentId);

      await appFetch({
        url: url,
        onSuccess: async (response) => {
          setDescription(response.description);
          setDescriptionTranslations(response.descriptionDetails || []);
          setSelectedContext(response.context);
          setSelectedLanguage(response.language);
          setSelectedOutlet(response.outletId ?? 0);
          setDateFrom(GetFormattedDate(response.validFrom));
          setDateTill(GetFormattedDate(response.validTill));
          setIsActive(response.isActive);
          setIsDefault(response.isDefault);
          setIsDefaultAccepted(response.isDefaultAccepted);
          setIsAcceptanceMandatory(response.isAcceptanceMandatory);
          setIsLocked(response.isLocked);
          setIsWarranty(response.isWarranty);
          setDocumentPdf(response.documentPdf || null);

          setModified({
            on: response.modifiedOn,
            byId: response.modifiedById,
            byName: response.modifiedByName,
            avatar: response.modifiedByAvatar,
          });

          if (response.documentBody) {
            const documentResponse = await fetch(response.documentBody);
            const documentContent = await documentResponse.text();

            const sanitizedContent = sanitizeAndFormatHTML(documentContent)

            setHtmlEditorValue(sanitizedContent || "");
            setOriginalHTMLDocument(sanitizedContent || "");
            setIsInitialRender(false)
          }

          if (response.documentPdf) {
            setOriginalPDFDocument(response.documentPdf);
          }
        },
        onFinalize: () => {
          setLoading(false);
        },
      });
    }

    getData();
  }, [documentId]);

  function isValidForm() {
    if (!description || description.trim().length === 0) {
      return false;
    }

    if (!selectedContext) {
      return false;
    }

    if (!selectedLanguage && !isDefault) {
      return false;
    }

    if (dateFrom && dateTill && new Date(dateFrom) > new Date(dateTill)) {
      return false;
    }

    if (
      (!htmlEditorValue || htmlEditorValue.trim().length === 0) &&
      !documentPdf
    ) {
      return false;
    }

    if (htmlEditorValue && documentPdf) {
      return false;
    }

    return true;
  }

  useEffect(() => {
      if (showPreview) {
        const timer = setTimeout(() => {
          setShowPreview(false);
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [showPreview]);

  async function submitHTMLFile() {
    const sasRequest = JSON.stringify({
      minutes: 5,
      entityType: "signableDocumentHtml",
      fileNames: [`${Uuidv4()}.html`],
    });

    const sanitizedHTML = sanitizeHTML(htmlEditorValue);

    const htmlBlob = new Blob([sanitizedHTML.trim()], {
      type: "text/html",
    });

    const blobPath = await uploadFile(htmlBlob, "text/html; charset=utf-8", sasRequest, "err");
    const file = {
      blobRelativePath: blobPath,
      type: 5,
    };

    return file;
  }

  async function submitPDFFile() {
    const sasRequest = JSON.stringify({
      minutes: 5,
      entityType: "signableDocumentPdf",
      fileNames: [`${Uuidv4()}.pdf`],
    });

    const blobPath = await uploadFile(
      documentPdf,
      "application/pdf",
      sasRequest,
      "err"
    );
    const file = {
      blobRelativePath: blobPath,
      type: 4,
    };

    return file;
  }

  function getFormData() {
    const formData = {
      description: description,
      descriptionDetails: descriptionTranslations.map((translation) => ({
        language: translation.language,
        description: translation.description,
      })),
      context: selectedContext,
      language: selectedLanguage,
      outletId: selectedOutlet ? selectedOutlet : null,
      validFrom: dateFrom,
      validTill: dateTill,
      isActive: isActive,
      isDefault: isDefault,
      isDefaultAccepted: isDefaultAccepted,
      isAcceptanceMandatory: isAcceptanceMandatory,
      isLocked: isLocked,
      isWarranty: isWarranty,
    };

    return formData;
  }

  async function handleEdit() {
    let file = null;
    if (htmlEditorValue.trim()) {
      if (
        !originalHTMLDocument ||
        originalHTMLDocument.trim() !== htmlEditorValue.trim()
      ) {
        file = await submitHTMLFile();
      }
    } else if (documentPdf) {
      if (originalPDFDocument !== documentPdf) {
        file = await submitPDFFile();
      }
    }

    const formData = getFormData();

    if (file) {
      formData.file = file;
    }

    const url = SignableDocumentByIdUrl(documentId);

    await appFetch({
      url: url,
      method: "PATCH",
      body: JSON.stringify(formData),
      onSuccess: () => {
        DisplayMessage(resources["SignableDocumentWasUpdated"], "success");
        setDialogOpen(false);
        setRedirect({
          pathname: "/signabledocuments",
          state: { refreshGrid: true },
        });
      },
      onFinalize: () => {
        setLoading(false);
      }
    });
  }

  async function handleCreate() {
    let file = null;

    if (documentPdf) {
      file = await submitPDFFile();
    }

    if (htmlEditorValue.trim()) {
      file = await submitHTMLFile();
    }

    const formData = getFormData();

    if (file) {
      formData.file = file;
    }

    const url = SignableDocumentsUrl();

    await appFetch({
      url: url,
      method: "POST",
      body: JSON.stringify(formData),
      onSuccess: () => {
        DisplayMessage(resources["SignableDocumentWasSaved"], "success");
        setDialogOpen(false);
        setRedirect({
          pathname: "/signabledocuments",
          state: { refreshGrid: true },
        });
      },
      onFinalize: () => {
        setLoading(false);
      }
    });

    
  }

  function handleSubmitButtonClick() {
    if(!hasViewedPreview && htmlEditorValue && !documentPdf) {
      setShowPreviewWarning(true);
      return;
    }

    handleSave()
  }

  async function handleSave() {
    setLoading(true);
    setShowPreviewWarning(false);

    if (documentId) {
      await handleEdit();
      return;
    }

    await handleCreate();
  }

  const handleHTMLchange = useCallback((value) => {
    setHasViewedPreview(false);
    setHtmlEditorValue(value);
  }, []);

  const handleMarkPreviewAsViewed = useCallback(() => {
    setHasViewedPreview(true);
  }, []);

  const handleFileChange = useCallback((files) => {
    const firstfile = files.length > 0 ? files[0] : null;
    if (firstfile) {
      setDocumentPdf(firstfile);
      setIsInitialRender((prev) => {
        if (prev) setOriginalPDFDocument(firstfile);
        return false;
      });
    } else {
      setDocumentPdf(null);
      setIsInitialRender(false);
    }
  }, []);

  async function uploadFile(file, type, body, error) {
    const sas = await GenerateSAS(body);
    if (!CheckIfSasIsValid(sas)) {
      DisplayMessage(resources["ErrorUploadingFileToStorage"], "error");
      return;
    }

    const blobPath = await UploadFileInStorage(file, sas[0], type, error);
    return blobPath;
  }

  const handlePreviewWarningClose = useCallback(() => {
    setShowPreview(true);
    setShowPreviewWarning(false);
    setHasViewedPreview(true);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    setRedirect({ pathname: "/signabledocuments" });
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    const formattedValue = value.trim();

    if (formattedValue.length <= 256) {
      setDescription(value);
    }

    setErrors(prev => ({
      ...prev,
      description: !formattedValue
    }));
  }, []);

  const handleSetDateFrom = useCallback((date) => {
    setDateFrom(date);
    setDateTill(prev => date > prev ? null : prev);
  }, []);

  const handleContextChange = useCallback((e) => {
    const value = e.target.value;

    if (CONTEXT_OPTIONS.some((option) => option.key === value)) {
      setSelectedContext(value);
      setErrors(prev => ({
        ...prev,
        context: false
      }));
    }
  }, []);

  const handleLanguageChange = useCallback((e) => {
    const value = e.target.value;

    if (LanguagesWithLanguageCode.some((language) => language.code === value)) {
      setSelectedLanguage(value);
      setErrors(prev => ({
        ...prev,
        language: false
      }));
    }
  }, []);

  const handleOutletChange = useCallback((e) => {
    setSelectedOutlet(e.target.value);
  }, []);

  const handleIsActiveChange = useCallback((e) => {
    setIsActive(e.target.checked);
  }, []);

  const handleIsDefaultChange = useCallback((e) => {
    const checked = e.target.checked;
    setIsDefault(checked);
    if (checked) {
      setSelectedLanguage(null);
      setErrors(prev => ({...prev, language: false}));
    } else if (!selectedLanguage) {
      setErrors(prev => ({...prev, language: true}));
    }
  }, [selectedLanguage]);

  const handleIsDefaultAcceptedChange = useCallback((e) => {
    setIsDefaultAccepted(e.target.checked);
  }, []);

  const handleIsAcceptanceMandatoryChange = useCallback((e) => {
    setIsAcceptanceMandatory(e.target.checked);
  }, []);

  const handleIsLockedChange = useCallback((e) => {
    setIsLocked(e.target.checked);
  }, []);

  const handleIsWarrantyChange = useCallback((e) => {
    setIsWarranty(e.target.checked);
  }, []);


  return (
    <Box>
      {redirect && <DelayedRedirect to={redirect} delay={250} />}
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBarComponent
          idName="viewMessageTemplate"
          title={
            documentId
              ? resources["EditSignableDocument"]
              : resources["CreateSignableDocument"]
          }
          resources={resources}
          handleClose={handleClose}
          handleSubmit={handleSubmitButtonClick}
          disabled={!isValidForm() || redirect}
        />

        {loading ? (
          <Box className="centered-loader fadeIn">
            <CircularProgress />
          </Box>
        ) : (
          <div className="container">
            <div className="py-4">
              <Paper elevation={3} className="p-4 mt-3 mb-3">
                <Box className="d-flex flex-column align-items-center">
                  <div
                    style={{
                      maxWidth: "800px",
                      width: "100%",
                      paddingTop: "1rem",
                    }}
                  >
                    <SignableDocumentsDescriptionSection
                      resources={resources}
                      description={description}
                      handleDescriptionChange={handleDescriptionChange}
                      descriptionTranslations={descriptionTranslations}
                      setDescriptionTranslations={setDescriptionTranslations}
                      documentId={documentId}
                      hasError={errors.description}
                    />

                    <EnhancedDropdownComponent
                      id="signable-documents-context-dropdown"
                      labelText={resources["Context"]}
                      value={selectedContext}
                      helperText={!documentId ? resources["SelectionOfContextIsMandatory"] : ""}
                      options={CONTEXT_OPTIONS}
                      error={errors.context}
                      handleChange={handleContextChange}
                      disabled={!!documentId}
                    />

                    <LanguageDropdown
                      resources={resources}
                      language={selectedLanguage}
                      noPlaceholder={false}
                      onLanguageChange={handleLanguageChange}
                      containerClass="helper-text-red"
                      addMarginBottom
                      disabled={isDefault}
                      required={!isDefault}
                      helperText={errors.language ? resources["SelectionOfLanguageIsMandatory"] : ""}
                    />

                    <EnhancedDropdownComponent
                      id="outlet-dropdown"
                      labelText={resources["Outlet"]}
                      value={selectedOutlet}
                      placeholder="All"
                      options={accessibleMoboOutletsOptions}
                      handleChange={handleOutletChange}
                    />

                    <SignableDocumentsDatePickerSection
                      resources={resources}
                      dateFrom={dateFrom}
                      setDateFrom={handleSetDateFrom}
                      dateTill={dateTill}
                      setDateTill={setDateTill}
                    />

                    <SignableDocumentsCheckboxesSection
                      isActive={isActive}
                      handleIsActiveChange={handleIsActiveChange}
                      isDefault={isDefault}
                      handleIsDefaultChange={handleIsDefaultChange}
                      isDefaultAccepted={isDefaultAccepted}
                      handleIsDefaultAcceptedChange={
                        handleIsDefaultAcceptedChange
                      }
                      isAcceptanceMandatory={isAcceptanceMandatory}
                      handleIsAcceptanceMandatoryChange={
                        handleIsAcceptanceMandatoryChange
                      }
                      isLocked={isLocked}
                      handleIsLockedChange={handleIsLockedChange}
                      isWarranty={isWarranty}
                      handleIsWarrantyChange={handleIsWarrantyChange}
                      resources={resources}
                    />

                    <SignableDocumentsDocumentEditorSection
                      resources={resources}
                      showPreview={showPreview}
                      markPreviewAsViewed={handleMarkPreviewAsViewed}
                      htmlEditorValue={htmlEditorValue}
                      handleHTMLchange={handleHTMLchange}
                      documentPdf={documentPdf}
                      setDocumentPdf={handleFileChange}
                      dealerGroupId={dealerGroupId}
                      existingPdfUrl={originalPDFDocument}
                      documentContext={selectedContext}
                    />
                  </div>
                </Box>
                {documentId && modified.on !== "" && (
                  <ModifiedByInfo
                    className="mt-5"
                    resources={resources}
                    modifiedByAvatar={modified.avatar}
                    modifiedOn={modified.on}
                    modifiedByName={modified.byName}
                    localeDate={true}
                  />
                )}
              </Paper>
            </div>
          </div>
        )}

        <SaveWithoutPreviewConfirmationModal
          open={showPreviewWarning}
          onClose={handlePreviewWarningClose}
          onConfirm={handleSave}
          resources={resources}
        />
      </Dialog>
    </Box>
  );
};

export default SignableDocumentsCreation;
